import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CounterpartiesList,
  Counterparty,
  CounterpartyListParams,
  CounterpartyRepository,
  CreateCounterparty,
  ValidateIBANObject,
} from '~/repositories/CounterpartyRepository';
import { useSessionStore } from '~/stores/Session';

import { createApiHook } from './utils/createApiHook';

export const useCounterparties = createApiHook<CounterpartiesList, CounterpartyListParams>(
  CounterpartyRepository.getAll,
  {
    triggerAutomatically: true,
    triggerOnSessionStoreSubscribe: true,
    includeQueryParams: true,
  }
);

export const useCounterparty = createApiHook<Counterparty, GetRequestType>(CounterpartyRepository.get);

export const useCreateCounterparty = createApiHook<Counterparty, CreateCounterparty>(CounterpartyRepository.create);

export const useDeleteCounterparty = createApiHook<object, GetRequestType>(CounterpartyRepository.delete);

export const useValidateIBAN = createApiHook<ValidateIBANObject, GetRequestType>(CounterpartyRepository.validateIBAN);

const formatCounterpartyDescription = (counterparty: Counterparty) => {
  return counterparty?.description && counterparty?.name
    ? `${counterparty.name} (${counterparty.description})`
    : counterparty?.description || counterparty?.name || counterparty?.wire?.beneficiaryName || 'Unnamed';
};

export const useCounterpartyDropdown = (counterpartyId?: string, initialQueryParams?: CounterpartyListParams) => {
  const platformId = useSessionStore((state) => state.currentPlatform?.id ?? state.currentUser?.defaultPlatformId);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [queryParams, setQueryParams] = useState<CounterpartyListParams | undefined>(initialQueryParams);

  useEffect(() => {
    setQueryParams(initialQueryParams);
  }, [initialQueryParams]);

  const isNumbericSearch = searchValue ? !isNaN(parseInt(searchValue, 10)) : false;
  const shouldUseMultiSearch = isNumbericSearch && Boolean(searchValue && searchValue.length > 0);
  const hasPlatformAndSearch = Boolean(platformId) && Boolean(searchValue);

  // Main query for non-numeric searches or when no search
  const counterpartiesQuery = useQuery({
    queryKey: [platformId, 'counterparties', queryParams],
    queryFn: () => CounterpartyRepository.getAll({ ...queryParams, platformId }),
    enabled: Boolean(platformId) && !shouldUseMultiSearch,
  });

  // Multi-field queries for numeric searches
  const accountNumberQuery = useQuery({
    queryKey: [platformId, 'counterparties', 'accountNumber', searchValue],
    queryFn: () => CounterpartyRepository.getAll({ accountNumber: searchValue!, platformId }),
    enabled: shouldUseMultiSearch && hasPlatformAndSearch,
  });

  const routingNumberQuery = useQuery({
    queryKey: [platformId, 'counterparties', 'routingNumber', searchValue],
    queryFn: () => CounterpartyRepository.getAll({ routingNumber: searchValue!, platformId }),
    enabled: shouldUseMultiSearch && hasPlatformAndSearch,
  });

  const descriptionQuery = useQuery({
    queryKey: [platformId, 'counterparties', 'description', searchValue],
    queryFn: () => CounterpartyRepository.getAll({ description: searchValue!, platformId }),
    enabled: shouldUseMultiSearch && hasPlatformAndSearch,
  });

  const activeCounterpartyQuery = useQuery({
    queryKey: [platformId, 'counterparty', counterpartyId],
    queryFn: () => CounterpartyRepository.get({ id: counterpartyId!, platformId }),
    enabled: Boolean(platformId && counterpartyId),
  });

  const getOption = useCallback(
    (counterparty: Counterparty | undefined) =>
      counterparty && {
        label: formatCounterpartyDescription(counterparty),
        small: `Rtn# ${counterparty.routingNumber} / Acc# ${counterparty.accountNumber}`,
        value: counterparty.id,
      },
    []
  );

  const counterpartyList = useMemo(() => {
    if (shouldUseMultiSearch) {
      const accountCounterparties = accountNumberQuery.data?.counterparties ?? [];
      const routingCounterparties = routingNumberQuery.data?.counterparties ?? [];
      const descriptionCounterparties = descriptionQuery.data?.counterparties ?? [];

      const allCounterparties: Counterparty[] = [
        ...accountCounterparties,
        ...routingCounterparties,
        ...descriptionCounterparties,
      ];

      return Array.from(new Map(allCounterparties.map((cp) => [cp.id, cp])).values());
    }

    return counterpartiesQuery.data?.counterparties ?? [];
  }, [
    shouldUseMultiSearch,
    accountNumberQuery.data,
    routingNumberQuery.data,
    descriptionQuery.data,
    counterpartiesQuery.data,
  ]);

  const isLoading = useMemo(() => {
    if (shouldUseMultiSearch) {
      return accountNumberQuery.isLoading || routingNumberQuery.isLoading || descriptionQuery.isLoading;
    }
    return counterpartiesQuery.isLoading && counterpartiesQuery.isFetching;
  }, [
    shouldUseMultiSearch,
    accountNumberQuery.isLoading,
    routingNumberQuery.isLoading,
    descriptionQuery.isLoading,
    counterpartiesQuery.isLoading,
    counterpartiesQuery.isFetching,
  ]);

  const options = useMemo(
    () =>
      counterpartyList
        .filter(Boolean)
        .map(getOption)
        .filter((option) => option !== undefined) ?? [],
    [counterpartyList, getOption]
  );

  const activeCounterparty = activeCounterpartyQuery.data;
  const currentCounterparty = counterpartyList?.find((counterparty) => counterparty.id === counterpartyId);
  const displayedCounterparty = currentCounterparty || activeCounterparty;

  const hiddenOptions = useMemo(
    () =>
      options.some((option) => option.value === displayedCounterparty?.id)
        ? []
        : displayedCounterparty
          ? ([displayedCounterparty]
              .filter(Boolean)
              .map(getOption)
              .filter((option) => option !== undefined) ?? [])
          : [],
    [displayedCounterparty, getOption, options]
  );

  const searchCounterparties = useCallback((searchVal?: string) => {
    setSearchValue(searchVal);

    if (searchVal && searchVal.length > 0) {
      const isNum = !isNaN(parseInt(searchVal, 10));
      if (!isNum) {
        setQueryParams({ description: searchVal });
      }
    } else {
      setSearchValue(undefined);
      setQueryParams(undefined);
    }
  }, []);

  return {
    counterparties: { counterparties: counterpartyList, hasMore: false },
    options,
    hiddenOptions,
    searchCounterparties,
    isLoading,
    activeCounterparty: displayedCounterparty,
  };
};
